// Audit-log viewer. Append-only audit_events table is the source of
// truth for "who did what when" — Compliance/Manager surface for
// quarterly access reviews and incident forensics.

import { useState } from 'react';
import { Search, RotateCw, FileSearch } from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardHeader } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Skeleton } from '../../components/ui/Skeleton';
import { useAudit } from '../../api/audit';

function isSeeded(details: unknown): boolean {
  return typeof details === 'object' && details !== null && (details as Record<string, unknown>).seeded === true;
}

function AuditTableRows({
  isLoading,
  rows,
}: {
  isLoading: boolean;
  rows: ReturnType<typeof useAudit>['data'];
}) {
  if (isLoading && !rows) {
    return (
      <>
        {Array.from({ length: 8 }).map((_, i) => (
          <tr key={`sk-${i}`} className="border-b border-border/60 last:border-0">
            <td className="px-5 py-3"><Skeleton width="9rem" /></td>
            <td className="px-5 py-3"><Skeleton width="10rem" /></td>
            <td className="px-5 py-3"><Skeleton width="8rem" /></td>
            <td className="px-5 py-3"><Skeleton width="14rem" /></td>
            <td className="px-5 py-3"><Skeleton width="6rem" /></td>
            <td className="px-5 py-3"><Skeleton width="3rem" /></td>
          </tr>
        ))}
      </>
    );
  }

  if (rows?.length) {
    return (
      <>
        {rows.map((e) => (
          <tr key={e.id} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
            <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">
              {new Date(e.at).toLocaleString()}
            </td>
            <td className="px-5 py-3 text-fg text-xs">{e.actor || '—'}</td>
            <td className="px-5 py-3">
              <span className="orb-code text-xs">{e.action}</span>
              {isSeeded(e.details) && (
                <span className="ml-2 inline-flex items-center rounded-full border border-border px-1.5 py-0.5 text-[10px] text-fg-muted">
                  Demo
                </span>
              )}
            </td>
            <td className="px-5 py-3 text-fg-muted text-xs font-mono break-all max-w-xs">{e.target || '—'}</td>
            <td className="px-5 py-3 text-fg-muted text-xs font-mono">{e.ip || '—'}</td>
            <td className="px-5 py-3 text-fg-muted text-xs">
              {e.details ? (
                <details>
                  <summary className="cursor-pointer hover:text-fg">view</summary>
                  <pre className="mt-2 p-2 rounded bg-bg-inset text-[10px] max-w-md overflow-auto">
                    {JSON.stringify(e.details, null, 2)}
                  </pre>
                </details>
              ) : (
                '—'
              )}
            </td>
          </tr>
        ))}
      </>
    );
  }

  return (
    <tr>
      <td colSpan={6} className="px-5 py-12 text-center text-fg-muted text-sm">
        <FileSearch className="h-8 w-8 mx-auto mb-2 text-fg-subtle" />
        No events match these filters.
      </td>
    </tr>
  );
}

export function AdminAudit() {
  const [actor, setActor] = useState('');
  const [action, setAction] = useState('');
  const [since, setSince] = useState('');
  const [hideSeeded, setHideSeeded] = useState(false);
  const [applied, setApplied] = useState<{ actor?: string; action?: string; since?: string }>({});
  const { data, isLoading, mutate } = useAudit({ ...applied, limit: 500 });
  const rows = hideSeeded ? data?.filter((e) => !isSeeded(e.details)) : data;

  const apply = () => {
    setApplied({
      actor: actor.trim() || undefined,
      action: action.trim() || undefined,
      since: since ? new Date(since).toISOString() : undefined,
    });
  };

  return (
    <AdminShell
      title="Audit log"
      description="Every administrative action is recorded here. Append-only by Postgres trigger — rows cannot be edited or deleted, even by org_admin."
    >
      <Card>
        <CardHeader title="Filter" description="Narrow the view by actor email, action prefix, or start date." />
        <div className="px-5 pb-5 grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input value={actor} onChange={(e) => setActor(e.target.value)} placeholder="actor email (ILIKE)" />
          <Input value={action} onChange={(e) => setAction(e.target.value)} placeholder="action prefix (e.g. license)" />
          <Input type="datetime-local" value={since} onChange={(e) => setSince(e.target.value)} />
          <button
            type="button"
            onClick={apply}
            className="inline-flex items-center justify-center gap-2 h-10 rounded-md bg-nebula text-white hover:bg-nebula/90 text-sm font-medium"
          >
            <Search className="h-4 w-4" />
            Apply
          </button>
        </div>
        <div className="px-5 pb-5">
          <label className="inline-flex items-center gap-2 text-sm text-fg-muted">
            <input type="checkbox" checked={hideSeeded} onChange={(e) => setHideSeeded(e.target.checked)} />
            Hide demo/seeded rows
          </label>
        </div>
      </Card>

      <Card>
        <CardHeader
          title={`Events (${rows?.length ?? 0})`}
          description="Newest first; capped at 500 rows per query."
          actions={
            <button
              type="button"
              onClick={() => mutate()}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-sm border border-border hover:bg-bg-hover text-fg-muted hover:text-fg"
            >
              <RotateCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted whitespace-nowrap">When</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Actor</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Action</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Target</th>
                <th className="px-5 py-3 font-medium text-fg-muted">IP</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Details</th>
              </tr>
            </thead>
            <tbody>
              <AuditTableRows isLoading={isLoading} rows={rows} />
            </tbody>
          </table>
        </div>
      </Card>
    </AdminShell>
  );
}
